import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, MapPin, Pencil } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { getQuirofanoDia, setQuirofanoDia } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import LugarQuirofanoSelect from './LugarQuirofanoSelect';
import MedicoVisitanteDia from './MedicoVisitanteDia';

/**
 * Barra del día en agendas de quirófano: el lugar (de «Lugares del quirófano»,
 * una lista de la clínica) — y en la agenda de externos el cirujano — es de
 * TODO el quirófano de ese día.
 * Aquí se ve y se cambia; las citas existentes del día se actualizan solas.
 *
 * Externos (dirección, 2026-09-08, punto 5): el cirujano es OBLIGATORIO y
 * SOLO de la lista de cirujanos dados de alta (se factura por él). Si el
 * día viene del histórico con la clínica en vez del cirujano, o sin él, el
 * backend lo marca (`cirujano_registrado: false`) y aquí se avisa en
 * ámbar para que se corrija con «Cambiar».
 *
 * Médico VISITANTE: el 5/10/2026 era un «Cirujano visitante» (uno, el
 * nombre) dentro del diálogo de este día; desde el 6/10/2026 es el «Médico
 * visitante» de CUALQUIER agenda, hasta dos con su especialidad, con su
 * propio componente (MedicoVisitanteDia) y su propia ruta. Aquí se pinta en
 * la barra, en el mismo sitio de antes, y ya no pide el lugar del día.
 *
 * Props: { agenda, date, refreshKey, onChanged }
 */
export default function QuirofanoDiaBar({ agenda, date, refreshKey = 0, onChanged }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [lugar, setLugar] = useState('');
  const [cirujano, setCirujano] = useState('');
  const [saving, setSaving] = useState(false);
  // Cirujanos del catálogo, con la clínica de la que viene cada uno.
  const [cirujanos, setCirujanos] = useState([]);

  const isExterna = !!agenda?.is_externa;

  useEffect(() => {
    if (!isExterna) return;
    api.get('/api/qx-catalogo', { params: { tipo: 'cirujano' } })
      .then((r) => setCirujanos(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [isExterna]);

  useEffect(() => {
    if (!agenda?.id || agenda?.tipo !== 'quirofano' || !date) {
      setConfig(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getQuirofanoDia(agenda.id, date)
      .then((cfg) => { if (!cancelled) setConfig(cfg && cfg.lugar ? cfg : null); })
      .catch(() => { if (!cancelled) setConfig(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agenda?.id, agenda?.tipo, date, refreshKey]);

  if (agenda?.tipo !== 'quirofano') return null;

  const openEdit = () => {
    setLugar(config?.lugar || '');
    // Un cirujano histórico que no está en la lista (el nombre de una clínica) no se
    // precarga: el desplegable no lo tendría y Guardar lo reenviaría tal cual.
    // Un día guardado con un nombre antiguo («Dr. Juan») se precarga con
    // el oficial de hoy («Dr. Juan Pérez Gil», 2026-09-08).
    setCirujano(config?.cirujano_registrado === false ? '' : (config?.cirujano_oficial || config?.cirujano || ''));
    setEditOpen(true);
  };

  const save = async () => {
    if (!lugar) {
      toast.error('Elige dónde es el quirófano');
      return;
    }
    if (isExterna && !cirujano.trim()) {
      toast.error('Elige el cirujano de la lista');
      return;
    }
    setSaving(true);
    try {
      const cfg = await setQuirofanoDia(agenda.id, {
        date,
        lugar,
        cirujano: isExterna ? cirujano.trim() : undefined,
      });
      setConfig(cfg);
      toast.success('Quirófano del día actualizado');
      setEditOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="flex flex-wrap items-center gap-2 rounded-lg border border-[#D9E3DD] bg-white px-3 py-2 text-sm"
        data-testid="quirofano-dia-bar"
      >
        <MapPin className="w-4 h-4 text-[#2E6E5E] shrink-0" />
        {loading ? (
          <span className="text-muted-foreground inline-flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando quirófano del día…
          </span>
        ) : config ? (
          <span className="text-[#13231E] inline-flex flex-wrap items-center gap-1.5">
            Quirófano del día en <span className="font-semibold">{config.lugar}</span>
            {isExterna && config.cirujano_registrado === false ? (
              <span className="text-amber-700 font-medium" data-testid="quirofano-dia-sin-cirujano">
                · Sin cirujano dado de alta
                {config.cirujano ? ` («${config.cirujano}» no está en la lista)` : ''}
                {' — pulsa Cambiar y elígelo'}
              </span>
            ) : config.cirujano ? (
              <>· Cirujano: <span className="font-semibold">{config.cirujano}</span></>
            ) : null}
            {/* De qué clínica es el externo, bien visible: es lo que hay que
                ver de un vistazo al mirar la agenda (2026-07-28). */}
            {config.clinica_externa && (
              <span
                className="inline-flex items-center rounded px-2 py-[2px] text-[10px] font-bold uppercase tracking-wide bg-[#284264] text-white"
                data-testid="quirofano-dia-clinica"
              >
                {config.clinica_externa}
              </span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground">
            Sin lugar asignado — se fijará con la primera cita del día
          </span>
        )}
        {/* Médico visitante (6/10/2026): con alguno, etiqueta de color que se
            ve de un vistazo; sin ninguno, un botón discreto para añadirlo. Lo
            lee y lo guarda él solo; no depende del lugar del día. */}
        <MedicoVisitanteDia agenda={agenda} date={date} refreshKey={refreshKey} />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 text-[#13231E]"
          onClick={openEdit}
          data-testid="quirofano-dia-editar"
        >
          <Pencil className="w-3.5 h-3.5 mr-1" />
          {config ? 'Cambiar' : 'Fijar ahora'}
        </Button>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm" data-testid="quirofano-dia-dialog">
          <DialogHeader>
            <DialogTitle>Quirófano del día — {agenda?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground -mt-1">
              Se aplica a TODO el quirófano de ese día; las citas existentes se actualizan.
            </p>
            <div>
              <Label>Lugar</Label>
              <LugarQuirofanoSelect value={lugar} onChange={setLugar} dataTestid="quirofano-dia-lugar" />
            </div>
            {isExterna && (
              <div>
                <Label>Cirujano externo <span className="text-red-600">*</span></Label>
                {/* Obligatorio y SOLO del catálogo (dirección, 2026-09-01 y
                    2026-09-08): con texto libre se escribía la clínica en vez
                    del cirujano y el registro de externos no podía facturar. */}
                <Select value={cirujano} onValueChange={setCirujano}>
                  <SelectTrigger className="mt-1.5" data-testid="quirofano-dia-cirujano">
                    <SelectValue placeholder="¿Quién opera?" />
                  </SelectTrigger>
                  <SelectContent>
                    {cirujanos.map((c) => (
                      <SelectItem key={c.id} value={c.nombre}>
                        {c.nombre}{c.clinica ? ` · ${c.clinica}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Solo cirujanos dados de alta. Si falta alguno, se añade en el Catálogo QX.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="quirofano-dia-guardar"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
